import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import {
  getPointingSystem,
  logClientError,
} from "../../utils/APICalls/APICalls";
import { getCookie } from "../../utils/CookieHelper";
import { COOKIE_ENUM, PagesEnum } from "../../utils/enums";
import envConfig from "../../utils/env.config";
import { AlertErrorType } from "../../utils/ErrorTypes";
import { getAllDisabledItems } from "../../utils/help/itemDetails/itemDetails";
import { getPageLink } from "../../utils/help/navigation/navigation";
import { parseJwt } from "../../utils/help/user/user";
import { createOffer } from "../../utils/OfferClass/OfferClassUtils/OfferCreator/offerCreator";
import { RestaurantPackageOffer } from "../../utils/types";
import {
  setHasRoundingMethodAndLevel,
  setPointingSystem,
} from "../redux/slices/cartAndLoyaltySlice";

import {
  sendGetPointingRequest,
  setCalledRestaurantInfoRequest,
} from "../redux/slices/requestLimiterSlice";
import { useAppDispatch, useAppSelector } from "./useReduxHooks";

const useRestaurantData = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const franchiseSlug = router.query.franchise as string;
  const restaurantInfo = useAppSelector((state) => state.config.restaurantInfo);
  const restaurantConfig = useAppSelector(
    (state) => state.config.restaurantConfig
  );
  const requestLimiter = useAppSelector((state) => state.requestLimiter);

  const token = useAppSelector((state) => state.userInfo.token);
  const itemStatusRedux = useAppSelector((state) => state.itemStatus);
  const selectedFranchise = useAppSelector(
    (state) => state.config.selectedFranchise
  );

  const customerShortId = useAppSelector(
    (state) => state.userInfo.customerShortId
  );

  const orderTypeParam = useAppSelector(
    (state) => state.queryParams.orderTypeParam
  );

  const [alertSent, setAlertSent] = useState(false);

  // Reset the restaurant info request flag when franchise changes
  useEffect(() => {
    dispatch(setCalledRestaurantInfoRequest(false));
  }, [selectedFranchise?.restaurantId]);

  const getAndSetPointingSystem = async (restaurantId: number) => {
    const allDisabledItemsIds = getAllDisabledItems(
      itemStatusRedux,
      orderTypeParam
    );

    await getPointingSystem(
      restaurantId,
      true,
      restaurantConfig?.hasOfflineMode ? customerShortId : undefined
    )
      .then((res) => {
        if (res.data) {
          res.data.restaurantPackageOffers = res.data.restaurantPackageOffers
            .map((restaurantPackageOffer: RestaurantPackageOffer) => {
              return {
                ...restaurantPackageOffer,
                packageOffer: {
                  ...restaurantPackageOffer.packageOffer,
                  isCartV2: true,
                  milestonePoints:
                    restaurantPackageOffer.pointingMilestone?.points,
                },
              };
            })
            .filter((restaurantPackageOffer: RestaurantPackageOffer) => {
              const offerV2 = createOffer(restaurantPackageOffer.packageOffer);

              return !offerV2.containsDisabledItems(allDisabledItemsIds);
            });
          dispatch(setPointingSystem(res.data));
        } else {
          dispatch(setPointingSystem(undefined));
        }
      })
      .catch((e) => {
        console.log("error getting point system ", e);
      });
  };

  useEffect(() => {
    if (restaurantInfo) {
      // Skip franchise validation for loyalty-card page - it uses franchise IDs, not slugs
      const isLoyaltyCardPage = router.pathname.includes(PagesEnum.AppleWallet);

      if (franchiseSlug && !isLoyaltyCardPage) {
        const currentFranchise = restaurantInfo.franchises.find(
          (fr) => fr.slug === franchiseSlug
        );
        if (!currentFranchise) {
          router.push(`${getPageLink(router, PagesEnum.Home).as}`);
        }
      }
      const hasRoundingMethodAndLevel =
        !!restaurantInfo.currencyRoundingMethod &&
        !!restaurantInfo.currencyRoundingLevel;
      dispatch(setHasRoundingMethodAndLevel(hasRoundingMethodAndLevel));
    }
    // The customDomain redirect lives in _app (CustomDomainRedirect) so it
    // happens before the page mounts — see prepits/project#4344.
  }, [restaurantInfo?.id]);

  useEffect(() => {
    const expectedRestaurantId = selectedFranchise?.restaurantId;
    const currentRestaurantId = restaurantInfo?.id;

    const restaurantId =
      expectedRestaurantId ||
      currentRestaurantId ||
      parseInt(envConfig.restaurantId!);
    const cookieToken = getCookie(COOKIE_ENUM.TOKEN);
    if (cookieToken) {
      getAndSetPointingSystem(restaurantId);
      dispatch(sendGetPointingRequest());

      const now = new Date();
      if (
        !alertSent &&
        requestLimiter.getPointingRequests.count > 10 &&
        Math.abs(
          now.valueOf() - requestLimiter.getPointingRequests.lastCalledAt
        ) /
          (1000 * 60) <
          1
      ) {
        const decodedToken = parseJwt(token);
        logClientError(
          `${AlertErrorType.GetPointing_Called_More_than_10_times}`,
          { customerId: decodedToken?.customer_id }
        );
        setAlertSent(true);
      }
    }
  }, [
    token,
    restaurantInfo?.id,
    selectedFranchise?.restaurantId,
    itemStatusRedux.disabledItemsIds.length,
    orderTypeParam,
  ]);
};

export default useRestaurantData;
